import type { ChangeEventHandler } from 'react';
import { memo, useCallback } from 'react';

import type { KcContext } from 'keycloakify/login/KcContext';

import { Button } from '@tripleten/design-system/button';

import { IconGoogle24 } from 'src/shared/components/icon-google-24/icon-google-24.js';
import { Input } from 'src/shared/components/input/input.js';
import { useFieldMessage } from 'src/shared/hooks/use-field-message.js';
import { useFormValidator } from 'src/shared/hooks/use-form-validator.js';
import { useSessionStorageState } from 'src/shared/hooks/use-session-storage-state.js';
import { internalPasswordLoginRestricted } from 'src/shared/utils/internal-password-login-restricted.js';
import { useGetMsg } from 'src/shared/utils/kc-messages.js';

import { PageInternalLoginHelp } from './__help/page-internal-login__help.js';
import { cnPageInternalLogin } from './page-internal-login.constants.js';

import './page-internal-login.css';

import './__form/page-internal-login__form.css';
import './__title/page-internal-login__title.css';

export interface PageInternalLoginProps {
    kcContext: KcContext.Login;
}

const validationFields = ['username', 'password'];

export const PageInternalLogin = memo<PageInternalLoginProps>(({ kcContext }) => {
    const [username = '', setUsername] = useSessionStorageState<string>('username', kcContext.login.username);

    const { msgStr, advancedMsgStr } = useGetMsg(kcContext);

    const mode = internalPasswordLoginRestricted(username) ? 'oauth' : 'password';

    const onUsernameChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (e) => setUsername(e.target.value),
        [setUsername],
    );

    const emailError = useFieldMessage(['email', 'username'], kcContext);
    const passwordError = useFieldMessage(['password'], kcContext);

    const [formErrors, resetFieldError, onSubmitValidated, submitDisabled] = useFormValidator(validationFields, {
        username: emailError,
        password: passwordError,
    });

    return (
        <div className={cnPageInternalLogin()}>
            <h1 className={cnPageInternalLogin('title')}>{msgStr('loginTitle')}</h1>
            <form
                action={kcContext.url.loginAction}
                method="post"
                className={cnPageInternalLogin('form')}
                onSubmit={onSubmitValidated}
            >
                <Input
                    type="email"
                    id="username"
                    name="username"
                    defaultValue={username}
                    label={msgStr('email')}
                    autoComplete="email"
                    className={cnPageInternalLogin('username')}
                    onChange={onUsernameChange}
                    onFocus={resetFieldError}
                    errorMessage={advancedMsgStr(formErrors['email'] ?? '')}
                />

                {mode === 'password' && (
                    <>
                        <Input
                            type="password"
                            id="password"
                            name="password"
                            label={msgStr('password')}
                            autoComplete="off"
                            className={cnPageInternalLogin('password')}
                            onFocus={resetFieldError}
                            errorMessage={advancedMsgStr(formErrors['password'] ?? '')}
                        />
                        <Button
                            variant="solid"
                            type="submit"
                            size="4"
                            className={cnPageInternalLogin('submit')}
                            disabled={submitDisabled}
                        >
                            {msgStr('toThePlatform')}
                        </Button>
                        {kcContext.realm.resetPasswordAllowed && (
                            <Button asChild variant="ghost" size="3" className={cnPageInternalLogin('reset-password')}>
                                <a href={kcContext.url.loginResetCredentialsUrl}>{msgStr('doForgotPassword')}</a>
                            </Button>
                        )}
                    </>
                )}
            </form>

            {mode === 'oauth' &&
                kcContext.social?.providers
                    ?.filter(({ alias }) => alias === 'google')
                    .map(({ providerId, loginUrl, displayName }) => (
                        <Button
                            key={providerId}
                            asChild
                            variant="outline"
                            size="4"
                            className={cnPageInternalLogin('oauth')}
                        >
                            <a href={loginUrl}>
                                <IconGoogle24 />
                                {`${msgStr('socialButtonTitle')} ${displayName}`}
                            </a>
                        </Button>
                    ))}

            <PageInternalLoginHelp />
        </div>
    );
});
